import { useState } from "react";
import { Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Printer, Wallet, Undo2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api, { formatApiError } from "@/lib/api";
import { docsAPI, fmtINR } from "@/lib/documents";
import { notifyDocument } from "@/components/documents/documentToast";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { AdvancePaymentInput, advanceTotal, advancePayload, emptyAdvance, methodLabel, validateAdvances, PAYMENT_METHODS, REFERENCE_REQUIRED } from "./AdvancePaymentInput";

/** Reservation detail card: lists advances taken at booking, prints receipts, adds more while the reservation is open. */
export function AdvancePaymentsCard({ reservation, onChanged }) {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState([emptyAdvance()]);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(null);
  const items = (reservation.advance_payments || []).filter((a) => a.status !== "void");
  const paid = Number(reservation.advance_paid_total ?? advanceTotal(items)) || 0;
  const grand = Number(reservation.grand_total) || 0;
  const canAdd = reservation.status === "confirmed" || reservation.status === "pending";
  const settlement = reservation.advance_settlement;
  const canSettle = (reservation.status === "cancelled" || reservation.status === "no_show") && paid > 0 && !settlement;
  const [settleOpen, setSettleOpen] = useState(false);
  const [sf, setSf] = useState({ action: "refund", amount: "", method: "", reference: "", note: "" });
  const [settling, setSettling] = useState(false);

  const settle = async () => {
    const refundAmt = sf.action === "refund" ? paid : sf.action === "forfeit" ? 0 : Number(sf.amount);
    if (sf.action === "partial" && !(refundAmt > 0 && refundAmt < paid)) { toast.error(`Partial refund must be between ₹0 and ${fmtINR(paid)}`); return; }
    const method = sf.method || items[0]?.method || "cash";
    if (refundAmt > 0 && REFERENCE_REQUIRED.has(method) && !sf.reference.trim()) { toast.error(`Reference number is required for ${methodLabel(method)} refunds`); return; }
    setSettling(true);
    try {
      const { data } = await api.post(`/pms/reservations/${reservation.id}/advance-settlement`, {
        action: sf.action, amount: sf.action === "partial" ? refundAmt : null, method: refundAmt > 0 ? method : null, reference: sf.reference.trim() || null, note: sf.note.trim() || null,
      });
      const st = data.advance_settlement;
      toast.success(st.refunded_amount ? `Refunded ${fmtINR(st.refunded_amount)}${st.forfeited_amount ? ` · forfeited ${fmtINR(st.forfeited_amount)}` : ""}` : `Advance ${fmtINR(st.forfeited_amount)} forfeited`);
      setSettleOpen(false); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setSettling(false);
  };

  const [voucherBusy, setVoucherBusy] = useState(false);
  const printVoucher = async () => {
    setVoucherBusy(true);
    try {
      let id = settlement?.refund_voucher_id, number = settlement?.refund_voucher_number;
      if (!id) {
        const { data } = await api.post(`/pms/reservations/${reservation.id}/refund-voucher`);
        id = data.voucher_id; number = data.voucher_number || data.receipt_number; onChanged?.();
      }
      notifyDocument(`Refund voucher ${number || ""} ready`, { url: docsAPI.receiptPdfUrl(id), filename: `RefundVoucher-${number || id}.pdf`, testId: "refund-voucher-toast" });
    } catch (e) { toast.error(formatApiError(e)); }
    setVoucherBusy(false);
  };

  const receipt = async (a) => {
    setPrinting(a.payment_id);
    try {
      const r = await docsAPI.createReceipt({ payment_id: a.payment_id });
      notifyDocument(`Receipt ${r.receipt_number} ready`, { url: docsAPI.receiptPdfUrl(r.id || r._id), filename: `Receipt-${r.receipt_number}.pdf`, testId: "advance-receipt-toast" });
    } catch (e) { toast.error(formatApiError(e)); }
    setPrinting(null);
  };

  const save = async () => {
    const err = validateAdvances(lines);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/pms/reservations/${reservation.id}/advance-payments`, {
        payments: advancePayload(lines, grand).advance_payments,
      });
      toast.success(`Advance recorded — total ${fmtINR(data.advance_paid_total)}`);
      setOpen(false); setLines([emptyAdvance()]); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <Card className="p-4" data-testid="advance-payments-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5"><Wallet size={14} strokeWidth={1.5} />Advance Payments</h3>
        {canAdd && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-advance-button"><Plus size={14} strokeWidth={1.5} className="mr-1" />Add advance</Button>}
        {canSettle && <Button size="sm" variant="outline" onClick={() => { setSf({ action: "refund", amount: "", method: items[0]?.method || "cash", reference: "", note: "" }); setSettleOpen(true); }} data-testid="settle-advance-button"><Undo2 size={14} strokeWidth={1.5} className="mr-1" />Refund / Forfeit</Button>}
      </div>
      {settlement && (
        <div className="mb-3 rounded-md border border-hocp-border bg-hocp-bgSubtle px-3 py-2 text-[12.5px] flex flex-wrap items-start justify-between gap-2" data-testid="advance-settlement">
          <div className="min-w-0">
            <div className="font-semibold text-hocp-textPrimary">{settlement.refunded_amount ? `Refunded ${fmtINR(settlement.refunded_amount)}${settlement.method ? ` via ${methodLabel(settlement.method)}` : ""}` : ""}{settlement.refunded_amount && settlement.forfeited_amount ? " · " : ""}{settlement.forfeited_amount ? `Forfeited ${fmtINR(settlement.forfeited_amount)}` : ""}</div>
            <div className="text-[11px] text-hocp-textMuted mono">{settlement.reference ? `${settlement.reference} · ` : ""}{(settlement.settled_at || "").slice(0, 16).replace("T", " ")} · {settlement.settled_by}{settlement.note ? ` · ${settlement.note}` : ""}{settlement.refund_voucher_number ? ` · ${settlement.refund_voucher_number}` : ""}</div>
          </div>
          {settlement.refunded_amount > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={printVoucher} disabled={voucherBusy} data-testid="print-refund-voucher">
                <Printer size={14} strokeWidth={1.5} className="mr-1" />{voucherBusy ? "Preparing…" : "Print Refund Voucher"}
              </Button>
              {settlement.refund_voucher_id && <DownloadPdfButton url={docsAPI.receiptPdfUrl(settlement.refund_voucher_id)} filename={`RefundVoucher-${settlement.refund_voucher_number || "voucher"}.pdf`} testId="download-refund-voucher" />}
            </div>
          )}
        </div>
      )}
      {canSettle && <div className="mb-3 rounded-md bg-[#FEF3C7] text-[#78530E] px-3 py-2 text-[12.5px]" data-testid="advance-settle-prompt">Reservation is {reservation.status === "no_show" ? "a no-show" : "cancelled"} — decide whether to refund or forfeit the advance of {fmtINR(paid)}.</div>}
      {items.length === 0 ? (
        <div className="text-[13px] text-hocp-textMuted" data-testid="advance-empty">No advance payment recorded.</div>
      ) : (
        <div className="divide-y divide-hocp-borderSubtle">
          {items.map((a) => (
            <div key={a.payment_id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`advance-row-${a.code}`}>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold mono">{fmtINR(a.amount)} <Badge tone="info">{methodLabel(a.method)}</Badge></div>
                <div className="text-[11px] text-hocp-textMuted mono truncate">{a.code}{a.reference ? ` · ${a.reference}` : ""} · {(a.received_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => receipt(a)} disabled={printing === a.payment_id} data-testid={`advance-receipt-${a.code}`}>
                <Printer size={14} strokeWidth={1.5} className="mr-1" />{printing === a.payment_id ? "Preparing…" : "Receipt"}
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2 text-[12.5px]">
        <div className="rounded-md bg-hocp-softIce px-3 py-2"><div className="text-[10px] mono uppercase text-hocp-textMuted">Advance paid</div><div className="mono font-semibold" data-testid="advance-total">{fmtINR(paid)}</div></div>
        <div className="rounded-md bg-hocp-bgSubtle border border-hocp-border px-3 py-2"><div className="text-[10px] mono uppercase text-hocp-textMuted">Balance at check-in</div><div className="mono font-semibold" data-testid="advance-balance">{grand ? fmtINR(Math.max(0, grand - paid)) : "—"}</div></div>
      </div>

      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="bg-hocp-bgCard border border-hocp-border md:max-w-[520px]" data-hocp-dialog data-testid="settle-advance-dialog">
          <DialogHeader><DialogTitle>Refund or forfeit advance</DialogTitle><DialogDescription>Reservation {reservation.code} · advance held {fmtINR(paid)}</DialogDescription></DialogHeader>
          <RadioGroup value={sf.action} onValueChange={(v) => setSf({ ...sf, action: v })} className="space-y-2" data-testid="settle-action">
            {[["refund", `Refund in full — ${fmtINR(paid)}`, "Money goes back to the guest"], ["partial", "Partial refund", "Refund part, forfeit the rest (e.g. cancellation fee)"], ["forfeit", `Forfeit in full — ${fmtINR(paid)}`, "Retained as cancellation / no-show charge"]].map(([v, t, d]) => (
              <label key={v} className="flex items-start gap-3 rounded-md border border-hocp-border p-3 cursor-pointer has-[[data-state=checked]]:border-hocp-mint">
                <RadioGroupItem value={v} id={`settle-${v}`} data-testid={`settle-action-${v}`} className="mt-0.5" />
                <span><span className="block text-[13px] font-semibold">{t}</span><span className="block text-[12px] text-hocp-textMuted">{d}</span></span>
              </label>
            ))}
          </RadioGroup>
          {sf.action === "partial" && (
            <div><Label className="text-[12px]">Refund amount (₹)</Label><Input type="number" min={0} step="0.01" value={sf.amount} onChange={(e) => setSf({ ...sf, amount: e.target.value })} className="mono" data-testid="settle-amount" />
              {Number(sf.amount) > 0 && Number(sf.amount) < paid && <div className="text-[11px] text-hocp-textMuted mt-1">Forfeited: {fmtINR(paid - Number(sf.amount))}</div>}</div>
          )}
          {sf.action !== "forfeit" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[12px]">Refund method</Label>
                <Select value={sf.method} onValueChange={(v) => setSf({ ...sf, method: v })}>
                  <SelectTrigger data-testid="settle-method"><SelectValue placeholder="Method" /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[12px]">Reference{REFERENCE_REQUIRED.has(sf.method) ? <span className="text-hocp-error"> *</span> : null}</Label><Input value={sf.reference} onChange={(e) => setSf({ ...sf, reference: e.target.value })} placeholder="UTR / reversal ref" data-testid="settle-reference" /></div>
            </div>
          )}
          <div><Label className="text-[12px]">Note</Label><Input value={sf.note} onChange={(e) => setSf({ ...sf, note: e.target.value })} placeholder="Cancellation policy applied…" data-testid="settle-note" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleOpen(false)}>Cancel</Button>
            <Button onClick={settle} disabled={settling} data-testid="settle-confirm">{settling ? "Saving…" : sf.action === "forfeit" ? "Forfeit advance" : "Record refund"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-hocp-bgCard border border-hocp-border md:max-w-[640px]" data-hocp-dialog data-testid="add-advance-dialog">
          <DialogHeader><DialogTitle>Record advance payment</DialogTitle><DialogDescription>Reservation {reservation.code} · booking total {fmtINR(grand)}</DialogDescription></DialogHeader>
          <AdvancePaymentInput value={lines} onChange={setLines} grandTotal={grand} nights={reservation.nights} alreadyPaid={paid} showToggle={false} testidPrefix="det-adv" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !lines.length} data-testid="save-advance-button">{saving ? "Saving…" : "Record payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
